import type { AgentCursor } from "@/stores/canvas-store";

/** What is known about one running session when a surface is chosen for it. */
export interface RunIdentity {
  conversationId?: string | null;
  executionId?: string | null;
  runtimeSessionId?: string | null;
  /** The conversation frame the server bound this run's cursor to. */
  conversationFrameId?: string | null;
}

/** A conversation frame that is on screen, with the run it currently shows. */
export interface VisibleConversation {
  frameId: string;
  conversationId?: string;
  executionId?: string;
  runtimeSessionId?: string;
}

export type RunSurface = "frame" | "cursor" | "toast";

/** The run a cursor stands for, given its resolved durable conversation. */
export function cursorRunIdentity(
  cursor: AgentCursor,
  conversationId: string | null,
): RunIdentity {
  return {
    conversationId,
    executionId: cursor.executionId,
    runtimeSessionId: cursor.sessionId,
    conversationFrameId: cursor.conversationFrameId,
  };
}

/** Whether two descriptions name the same running session. */
export function sameRun(a: RunIdentity, b: RunIdentity): boolean {
  // A root agent and each native subagent own separate durable conversations,
  // so once both sides name one, no weaker identity may override it.
  if (a.conversationId && b.conversationId) {
    return a.conversationId === b.conversationId;
  }
  if (
    a.conversationFrameId &&
    a.conversationFrameId === b.conversationFrameId
  ) {
    return true;
  }
  if (a.executionId && a.executionId === b.executionId) return true;
  return !!a.runtimeSessionId && a.runtimeSessionId === b.runtimeSessionId;
}

/** The id of the visible conversation frame that shows this exact run. */
export function conversationFrameForRun(
  run: RunIdentity,
  visible: readonly VisibleConversation[],
): string | null {
  return (
    visible.find((frame) =>
      sameRun(run, {
        conversationId: frame.conversationId,
        executionId: frame.executionId,
        runtimeSessionId: frame.runtimeSessionId,
        conversationFrameId: frame.frameId,
      }),
    )?.frameId ?? null
  );
}

/**
 * One surface per running session: its visible conversation frame, else its
 * own agent cursor while results are undocked, else the tray toast. A desktop
 * approval belongs to the agent cursor that asks it on screen, docked or not;
 * the frame may show it too.
 */
export function runSurfaceOwner(
  run: RunIdentity,
  {
    visibleConversations,
    dockEnabled,
    hasOwnCursor,
    desktopApproval = false,
  }: {
    visibleConversations: readonly VisibleConversation[];
    dockEnabled: boolean;
    hasOwnCursor: boolean;
    desktopApproval?: boolean;
  },
): RunSurface {
  if (desktopApproval && hasOwnCursor) return "cursor";
  if (conversationFrameForRun(run, visibleConversations) !== null) {
    return "frame";
  }
  return !dockEnabled && hasOwnCursor ? "cursor" : "toast";
}
